/**
 * @jsxRuntime classic
 * @jsx jsx
 */

import { useEffect, useMemo } from 'react';

import { css, jsx } from '@compiled/react';
import { di } from 'react-magnetic-di';
import { token } from '@atlaskit/tokens';
import { PopupTrigger } from '@atlassian/entry-point-popup';

import { createAssistantConfig } from '../common/utils';
import { useWidgetConfig } from '../services/use-widget-config';
import { ChatBubbleButton } from './chat-bubble-button';
import { RovoServiceAgentEntrypoint } from './entrypoint';

const HELP_BUBBLE_ZINDEX = 1000;

const helpStyles = css({
    position: 'fixed',
    zIndex: HELP_BUBBLE_ZINDEX,
    bottom: token('space.200', '16px'),
    right: token('space.200', '16px'),
    alignItems: 'flex-end',
    flexDirection: 'column-reverse',
    display: 'flex',
});

interface WidgetBubbleProps {
    siteId: string;
    widgetId: string;
    jiraDisplayUrl: string;
    maxHeight?: number;
}

export const RovoServiceAgentChatWidgetBubble = ({
    siteId,
    widgetId,
    jiraDisplayUrl,
    maxHeight = 704,
}: WidgetBubbleProps) => {
    di(useWidgetConfig, PopupTrigger);

    const { widgetConfig, fetch, isLoading: isWidgetLoading } = useWidgetConfig(widgetId);

    useEffect(() => {
        if (!widgetConfig && !isWidgetLoading) {
            fetch().catch(() => {
                /** noop */
            });
        }
    }, [widgetConfig, fetch, isWidgetLoading]);

    const entryPointProps = useMemo(
        () => ({
            maxHeight,
            config: createAssistantConfig(siteId, widgetId, jiraDisplayUrl),
        }),
        [siteId, widgetId, maxHeight, jiraDisplayUrl]
    );

    if (isWidgetLoading || !widgetConfig?.isWidgetEnabled) {
        return null;
    }

    return (
        <div css={helpStyles} data-testid="rovo-service-agent-chat-widget-bubble">
            <PopupTrigger
                placement="top-end"
                entryPoint={RovoServiceAgentEntrypoint}
                entryPointProps={entryPointProps}
                trigger={(triggerProps) => <ChatBubbleButton {...triggerProps} />}
            />
        </div>
    );
};
